import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import SignatureLines from './SignatureLines';
import './ReportPrintView.css';
import { formatDay } from '../utils/dates';
import Loading from './Loading';
import { useFileName, reportName } from '../utils/fileName';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);
const pctStr = (n) => n == null ? '—' : `${n.toFixed(1)}%`;

const STATUS_LABELS = {
  draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', expired: 'Expirada',
};

const NCF_TIPO_NAMES = {
  E31: 'Factura de Crédito Fiscal', E32: 'Factura de Consumo', E33: 'Nota de Débito', E34: 'Nota de Crédito',
};
const NCF_STATUS_LABELS = {
  issued: 'Emitida', partially_paid: 'Parcial', paid: 'Pagada', void: 'Anulada', pending_dgii: 'En DGII',
};

const CONDUCE_STATUS_LABELS = { pending: 'Por entregar', delivered: 'Entregado' };

const REPORT_TITLES = {
  'sales-detail': 'Ventas Detalladas',
  'sales-by-customer': 'Ventas por Cliente',
  'sales-by-product': 'Ventas por Producto',
  'new-customers': 'Clientes Nuevos',
  'inactive-customers': 'Clientes Inactivos',
  'ar-aging': 'Antigüedad de Cuentas por Cobrar (Aging)',
  'customer-statement': 'Estado de Cuenta por Cliente',
  'outstanding-invoices': 'Facturas Pendientes',
  'payment-register': 'Registro de Pagos',
  'receipts-by-client': 'Recibos de Ingreso por Cliente',
  'collections': 'Cobros',
  'dso': 'Días Promedio de Pago (DSO)',
  'quotes-by-status': 'Cotizaciones por Estado',
  'conduces': 'Reporte de Conduces',
  'expenses-summary': 'Resumen de Gastos',
  'expenses-by-category': 'Gastos por Categoría',
  'product-margin': 'Margen Bruto por Producto',
  'profit-and-loss': 'Estado de Resultados (P&L Simplificado)',
  'ncf-listing': 'Listado de Facturas con NCF',
  'dgii-607': '607 — Ventas',
  'dgii-608': '608 — Comprobantes Anulados',
  'itbis-summary': 'Resumen de ITBIS',
  'repeat-client-rate': 'Tasa de Clientes Recurrentes',
  'revenue-concentration': 'Concentración de Ingresos (Top Clientes)',
  'reorder-cycle': 'Ciclo Promedio de Recompra',
  'itbis-payable': 'ITBIS a Pagar (Próximo Período)',
  'avg-invoice-value': 'Ingreso Promedio por Factura',
};

// Each report fetches from the same backend endpoints the live Reportes tabs
// use, and renders the same shape of table — this is just a letterhead
// wrapper around identical data, not a separate report engine.
async function fetchReportData(id, { from, to, clientId, status, tipo }) {
  const params = { from, to };
  switch (id) {
    case 'conduces': return (await API.get('/reports/conduces', { params: { ...params, client: clientId, status } })).data;
    case 'sales-detail': return (await API.get('/reports/sales-detail', { params })).data;
    case 'sales-by-customer': return (await API.get('/reports/by-client', { params })).data;
    case 'sales-by-product': return (await API.get('/reports/by-product', { params })).data;
    case 'new-customers': return (await API.get('/reports/customers-activity', { params })).data.newCustomers;
    case 'inactive-customers': return (await API.get('/reports/customers-activity', { params })).data.inactiveCustomers;
    case 'ar-aging': return (await API.get('/reports/ar-aging')).data;
    case 'customer-statement': return (await API.get(`/reports/customer-statement/${clientId}`)).data;
    case 'outstanding-invoices': return (await API.get('/reports/outstanding')).data;
    case 'payment-register': return (await API.get('/reports/payments', { params })).data;
    case 'receipts-by-client': return (await API.get('/reports/receipts-by-client', { params: { ...params, client: clientId || undefined } })).data;
    case 'collections': return (await API.get('/reports/collections', { params: { ...params, groupBy: 'day' } })).data;
    case 'dso': return (await API.get('/reports/dso', { params })).data;
    case 'quotes-by-status': return (await API.get('/reports/quotes-status', { params })).data;
    case 'expenses-summary': return (await API.get('/reports/expenses-summary', { params })).data;
    case 'expenses-by-category': return (await API.get('/reports/expenses-by-category', { params })).data;
    case 'product-margin': return (await API.get('/reports/profit-margin', { params })).data;
    case 'profit-and-loss': return (await API.get('/reports/profit-loss', { params })).data;
    case 'ncf-listing': return (await API.get('/reports/ncf-listing', { params: { ...params, tipo } })).data;
    case 'dgii-607': return (await API.get('/reports/sales', { params })).data;
    case 'dgii-608': return (await API.get('/reports/voided', { params })).data;
    case 'itbis-summary': return (await API.get('/reports/itbis-summary', { params })).data;
    case 'repeat-client-rate': return (await API.get('/reports/repeat-client-rate', { params })).data;
    case 'revenue-concentration': return (await API.get('/reports/revenue-concentration', { params })).data;
    case 'reorder-cycle': return (await API.get('/reports/reorder-cycle')).data;
    case 'itbis-payable': return (await API.get('/reports/itbis-payable', { params })).data;
    case 'avg-invoice-value': return (await API.get('/reports/avg-invoice-value', { params })).data;
    default: throw new Error('Tipo de reporte no reconocido.');
  }
}

// The 7 pure-KPI reports also fetch a "-detail" endpoint in parallel (see
// backend/routes/reports.js) supplying 6-month trend + breakdown data used
// to render 3 print-safe charts and a deterministic (non-LLM) insight
// sentence below the big KPI number, instead of just a bare number.
async function fetchDetailData(id, { from, to }) {
  const params = { from, to };
  switch (id) {
    case 'dso': return (await API.get('/reports/dso-detail', { params })).data;
    case 'expenses-summary': return (await API.get('/reports/expenses-summary-detail', { params })).data;
    case 'repeat-client-rate': return (await API.get('/reports/repeat-client-rate-detail', { params })).data;
    case 'revenue-concentration': return (await API.get('/reports/revenue-concentration-detail', { params })).data;
    case 'reorder-cycle': return (await API.get('/reports/reorder-cycle-detail')).data;
    case 'itbis-payable': return (await API.get('/reports/itbis-payable-detail', { params })).data;
    case 'avg-invoice-value': return (await API.get('/reports/avg-invoice-value-detail', { params })).data;
    default: return null;
  }
}

const CHART_COLORS = { teal: '#ED1923', darkTeal: '#B01420', slate: '#8892a0', rust: '#E07A5F' };

const lineOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { font: { size: 9 } } }, x: { ticks: { font: { size: 9 } } } },
};
const barOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { font: { size: 9 } } }, x: { ticks: { font: { size: 9 } } } },
};
const hBarOptions = { ...barOptions, indexAxis: 'y' };
const stackedBarOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: true, position: 'bottom', labels: { font: { size: 9 }, boxWidth: 10 } } },
  scales: {
    y: { beginAtZero: true, stacked: true, ticks: { font: { size: 9 } } },
    x: { stacked: true, ticks: { font: { size: 9 } } },
  },
};

// Compares the last two non-null points in a trend series (month-over-month).
// `fromZero` flags a jump from a zero baseline, where a percentage change is
// undefined/infinite — the caller should describe the absolute jump instead.
function momChange(trend, key) {
  const vals = (trend || []).filter(t => t[key] != null);
  if (vals.length < 2) return null;
  const last = vals[vals.length - 1][key];
  const prev = vals[vals.length - 2][key];
  if (prev === 0) return { last, prev, pct: null, fromZero: last !== 0 };
  return { last, prev, pct: ((last - prev) / Math.abs(prev)) * 100, fromZero: false };
}

// Turns a momChange() result into a Spanish sentence fragment describing the
// direction of change, using `fmt` to render each value (e.g. money, days, %).
// `fromZeroVerb` lets plural subjects ("Los gastos pasaron...") agree in
// number, since the default is written for singular subjects ("El DSO pasó...").
function trendPhrase(chg, fmt, { fromZeroVerb = 'pasó' } = {}) {
  if (!chg) return null;
  if (chg.fromZero) return `${fromZeroVerb} de ${fmt(chg.prev)} a ${fmt(chg.last)} respecto al mes anterior`;
  if (chg.pct == null || chg.pct === 0) return `se mantuvo estable en ${fmt(chg.last)}`;
  const dir = chg.pct > 0 ? `subió ${chg.pct.toFixed(0)}%` : `bajó ${Math.abs(chg.pct).toFixed(0)}%`;
  return `${dir} respecto al mes anterior (${fmt(chg.last)} vs. ${fmt(chg.prev)})`;
}

const KPI_CHART_BUILDERS = {
  dso: ({ trend, distribution, topSlow }) => ([
    { title: 'Tendencia DSO (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: 'DSO (días)', data: trend.map(t => t.dso), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Facturas Pagadas por Mes', el: <Bar data={{ labels: trend.map(t => t.month), datasets: [{ data: trend.map(t => t.count), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
    { title: 'Distribución de Pagos', el: <Bar data={{ labels: distribution.map(d => d.bucket), datasets: [{ data: distribution.map(d => d.count), backgroundColor: CHART_COLORS.darkTeal }] }} options={barOptions} /> },
    { title: 'Clientes Más Lentos', el: <Bar data={{ labels: topSlow.map(s => s.client), datasets: [{ data: topSlow.map(s => s.avgDays), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
  ]),
  'expenses-summary': ({ trend, byCategory, topVendors, byMethod }) => ([
    { title: 'Tendencia de Gastos (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: 'Total', data: trend.map(t => t.total), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Gastos por Categoría', el: <Bar data={{ labels: byCategory.map(c => c.category), datasets: [{ data: byCategory.map(c => c.total), backgroundColor: CHART_COLORS.darkTeal }] }} options={hBarOptions} /> },
    { title: 'Principales Proveedores', el: <Bar data={{ labels: topVendors.map(v => v.vendor), datasets: [{ data: topVendors.map(v => v.total), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
    { title: 'Gastos por Método de Pago', el: <Bar data={{ labels: byMethod.map(m => m.method), datasets: [{ data: byMethod.map(m => m.total), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
  ]),
  'repeat-client-rate': ({ trend, newVsRepeat, frequencyDistribution, topRepeatClients }) => ([
    { title: 'Tendencia (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: '% Recurrentes', data: trend.map(t => t.rate), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Nuevos vs. Recurrentes', el: <Bar data={{ labels: newVsRepeat.map(n => n.month), datasets: [{ label: 'Nuevos', data: newVsRepeat.map(n => n.new), backgroundColor: CHART_COLORS.slate }, { label: 'Recurrentes', data: newVsRepeat.map(n => n.repeat), backgroundColor: CHART_COLORS.darkTeal }] }} options={stackedBarOptions} /> },
    { title: 'Frecuencia de Compra', el: <Bar data={{ labels: frequencyDistribution.map(f => f.bucket), datasets: [{ data: frequencyDistribution.map(f => f.count), backgroundColor: CHART_COLORS.teal }] }} options={barOptions} /> },
    { title: 'Clientes Más Recurrentes', el: <Bar data={{ labels: topRepeatClients.map(c => c.name), datasets: [{ data: topRepeatClients.map(c => c.count), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
  ]),
  'revenue-concentration': ({ trend, distribution, topClients }) => ([
    { title: 'Concentración (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: '% Top 5', data: trend.map(t => t.concentration), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Ingreso Total por Mes', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: 'Ingreso', data: trend.map(t => t.total), borderColor: CHART_COLORS.darkTeal, backgroundColor: 'rgba(14,140,134,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Concentración por Rango', el: <Bar data={{ labels: distribution.map(d => d.bucket), datasets: [{ data: distribution.map(d => d.pct), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
    { title: 'Top Clientes por Ingreso', el: <Bar data={{ labels: topClients.map(c => c.name), datasets: [{ data: topClients.map(c => c.total), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
  ]),
  'reorder-cycle': ({ distribution, topFrequent, topSlow, volumeTrend }) => ([
    { title: 'Pedidos por Mes (6 meses)', el: <Line data={{ labels: volumeTrend.map(t => t.month), datasets: [{ label: '# Pedidos', data: volumeTrend.map(t => t.count), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Distribución del Ciclo', el: <Bar data={{ labels: distribution.map(d => d.bucket), datasets: [{ data: distribution.map(d => d.count), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
    { title: 'Clientes Más Frecuentes', el: <Bar data={{ labels: topFrequent.map(c => c.client), datasets: [{ data: topFrequent.map(c => c.avgDays), backgroundColor: CHART_COLORS.darkTeal }] }} options={hBarOptions} /> },
    { title: 'Ciclos Más Largos', el: <Bar data={{ labels: topSlow.map(c => c.client), datasets: [{ data: topSlow.map(c => c.avgDays), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
  ]),
  'itbis-payable': ({ trend, byRate, topContributors, byTipoEcf }) => ([
    { title: 'Tendencia ITBIS (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: 'ITBIS', data: trend.map(t => t.itbis), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'ITBIS por Tasa', el: <Bar data={{ labels: byRate.map(r => r.bucket), datasets: [{ data: byRate.map(r => r.itbis), backgroundColor: CHART_COLORS.darkTeal }] }} options={barOptions} /> },
    { title: 'ITBIS por Tipo eCF', el: <Bar data={{ labels: byTipoEcf.map(t => t.tipoEcf), datasets: [{ data: byTipoEcf.map(t => t.itbis), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
    { title: 'Principales Contribuyentes', el: <Bar data={{ labels: topContributors.map(c => c.name), datasets: [{ data: topContributors.map(c => c.itbis), backgroundColor: CHART_COLORS.rust }] }} options={hBarOptions} /> },
  ]),
  'avg-invoice-value': ({ trend, distribution, byTipoEcf }) => ([
    { title: 'Tendencia (6 meses)', el: <Line data={{ labels: trend.map(t => t.month), datasets: [{ label: 'Promedio', data: trend.map(t => t.avg), borderColor: CHART_COLORS.teal, backgroundColor: 'rgba(237,25,35,0.15)', tension: 0.3 }] }} options={lineOptions} /> },
    { title: 'Facturas Emitidas por Mes', el: <Bar data={{ labels: trend.map(t => t.month), datasets: [{ data: trend.map(t => t.count), backgroundColor: CHART_COLORS.slate }] }} options={barOptions} /> },
    { title: 'Distribución por Monto', el: <Bar data={{ labels: distribution.map(d => d.bucket), datasets: [{ data: distribution.map(d => d.count), backgroundColor: CHART_COLORS.darkTeal }] }} options={barOptions} /> },
    { title: 'Promedio por Tipo eCF', el: <Bar data={{ labels: byTipoEcf.map(t => t.tipoEcf), datasets: [{ data: byTipoEcf.map(t => t.avg), backgroundColor: CHART_COLORS.rust }] }} options={barOptions} /> },
  ]),
};

const KPI_INSIGHT_BUILDERS = {
  dso: ({ trend, topSlow }) => {
    const chg = momChange(trend, 'dso');
    const parts = [];
    if (chg) parts.push(`El DSO ${trendPhrase(chg, v => `${v.toFixed(1)} días`)}.`);
    if (topSlow?.length) parts.push(`${topSlow[0].client} tiene el ciclo de cobro más lento, con ${topSlow[0].avgDays.toFixed(0)} días en promedio.`);
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
  'expenses-summary': ({ trend, byCategory }) => {
    const chg = momChange(trend, 'total');
    const parts = [];
    if (chg) parts.push(`Los gastos ${trendPhrase(chg, money, { fromZeroVerb: 'pasaron' })}.`);
    if (byCategory?.length) parts.push(`La categoría con mayor gasto es "${byCategory[0].category}", con ${money(byCategory[0].total)}.`);
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
  'repeat-client-rate': ({ trend, frequencyDistribution }) => {
    const chg = momChange(trend, 'rate');
    const parts = [];
    if (chg) parts.push(`La tasa de clientes recurrentes ${trendPhrase(chg, v => `${v.toFixed(0)}%`)}.`);
    const loyal = frequencyDistribution?.find(b => b.bucket === '4+ compras');
    if (loyal?.count) parts.push(`${loyal.count} cliente(s) han comprado 4 veces o más.`);
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
  'revenue-concentration': ({ trend, distribution }) => {
    const chg = momChange(trend, 'concentration');
    const parts = [];
    if (chg) parts.push(`La concentración de ingresos ${trendPhrase(chg, v => `${v.toFixed(0)}%`)}.`);
    const top5 = distribution?.find(b => b.bucket === 'Top 5');
    if (top5) parts.push(top5.pct > 60 ? 'La dependencia de pocos clientes es alta — vale la pena diversificar la cartera.' : 'La cartera de clientes está razonablemente diversificada.');
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
  'reorder-cycle': ({ topFrequent, topSlow }) => {
    const parts = [];
    if (topFrequent?.length) parts.push(`${topFrequent[0].client} compra con más frecuencia, cada ${topFrequent[0].avgDays.toFixed(0)} días en promedio.`);
    if (topSlow?.length) parts.push(`${topSlow[0].client} tiene el ciclo de recompra más largo, cada ${topSlow[0].avgDays.toFixed(0)} días.`);
    return parts.join(' ') || 'Aún no hay suficientes clientes con pedidos repetidos para este análisis.';
  },
  'itbis-payable': ({ trend, topContributors }) => {
    const chg = momChange(trend, 'itbis');
    const parts = [];
    if (chg) parts.push(`El ITBIS generado ${trendPhrase(chg, money)}.`);
    if (topContributors?.length) parts.push(`${topContributors[0].name} es el cliente que más ITBIS generó en este período, con ${money(topContributors[0].itbis)}.`);
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
  'avg-invoice-value': ({ trend, byTipoEcf }) => {
    const chg = momChange(trend, 'avg');
    const parts = [];
    if (chg) parts.push(`El valor promedio por factura ${trendPhrase(chg, money)}.`);
    if (byTipoEcf?.length > 1) {
      const top = [...byTipoEcf].sort((a, b) => b.avg - a.avg)[0];
      parts.push(`Las facturas tipo ${top.tipoEcf} tienen el valor promedio más alto (${money(top.avg)}).`);
    }
    return parts.join(' ') || 'Aún no hay suficientes datos para un análisis de tendencia.';
  },
};

function KpiDetailSection({ id, detail }) {
  const buildCharts = KPI_CHART_BUILDERS[id];
  const buildInsight = KPI_INSIGHT_BUILDERS[id];
  if (!buildCharts || !detail) return null;
  const charts = buildCharts(detail);
  return (
    <div className="report-kpi-detail">
      <div className="report-charts-grid">
        {charts.map((c, i) => (
          <div className="report-chart-box" key={i}>
            <div className="report-chart-title">{c.title}</div>
            <div className="report-chart-canvas-wrap">{c.el}</div>
          </div>
        ))}
      </div>
      <div className="report-insight"><strong>Análisis:</strong> {buildInsight(detail)}</div>
    </div>
  );
}

const SALES_METHOD_LABELS = {
  efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque', saldo: 'Saldo a favor',
  notas: 'Notas de crédito', ajuste: 'Saldado sin cobro (ajuste)', pendiente: 'Pendiente de cobro (crédito)',
};

const RECEIPT_METHOD_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque', saldo: 'Saldo a favor' };

function ReportBody({ id, data }) {
  switch (id) {
    // Every factura in the range, oldest first, with totals; then how they were paid (as of today), adding up to the total.
    case 'sales-detail': {
      const rows = data.rows || [];
      const t = data.totals || {};
      return (
        <>
          <table className="report-table">
            <thead>
              <tr>
                <th>Fecha</th><th>Factura</th><th>NCF</th><th>Cliente</th>
                <th className="text-end">Subtotal</th><th className="text-end">ITBIS</th><th className="text-end">Total</th>
                <th className="text-end">Cobrado</th><th className="text-end">Pendiente</th><th>Forma de pago</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{dateStr(r.date)}</td><td>{r.number}</td><td>{r.ncf || '—'}</td><td>{r.client}</td>
                  <td className="text-end">{money(r.subtotal)}</td><td className="text-end">{money(r.itbis)}</td><td className="text-end">{money(r.total)}</td>
                  <td className="text-end">{money(r.paid + r.credited)}</td><td className="text-end">{r.pending > 0.005 ? money(r.pending) : '—'}</td>
                  <td>{[...r.methods.map(m => SALES_METHOD_LABELS[m] || m), ...(r.credited > 0.005 ? ['Nota de crédito'] : [])].join(', ') || (r.pending > 0.005 ? 'Crédito' : '—')}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="text-center">Sin facturas en este rango.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td colSpan={4}>Total ({rows.length} {rows.length === 1 ? 'factura' : 'facturas'})</td>
                  <td className="text-end">{money(t.subtotal)}</td><td className="text-end">{money(t.itbis)}</td><td className="text-end">{money(t.total)}</td>
                  <td className="text-end">{money(t.paid + t.credited)}</td><td className="text-end">{money(t.pending)}</td><td />
                </tr>
              </tfoot>
            )}
          </table>
          {rows.length > 0 && (
            <table className="report-table" style={{ width: 'auto', minWidth: '45%', marginLeft: 'auto', marginTop: 16 }}>
              <tbody>
                {(data.byMethod || []).map(m => (
                  <tr key={m.key}><td>{SALES_METHOD_LABELS[m.key] || m.key}:</td><td className="text-end">{money(m.amount)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, fontSize: '1.05em' }}><td>Total:</td><td className="text-end">{money(t.total)}</td></tr>
              </tfoot>
            </table>
          )}
        </>
      );
    }

    case 'sales-by-customer':
      return (
        <table className="report-table">
          <thead><tr><th>Cliente</th><th className="text-end"># Facturas</th><th className="text-end">Total</th></tr></thead>
          <tbody>
            {data.map((c, i) => <tr key={i}><td>{c.name}</td><td className="text-end">{c.invoiceCount}</td><td className="text-end">{money(c.total)}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin datos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'sales-by-product':
      return (
        <table className="report-table">
          <thead><tr><th>Producto</th><th className="text-end">Cantidad</th><th className="text-end">Ingresos</th></tr></thead>
          <tbody>
            {data.map((p, i) => <tr key={i}><td>{p.name}</td><td className="text-end">{p.quantity}</td><td className="text-end">{money(p.revenue)}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin datos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'new-customers':
      return (
        <table className="report-table">
          <thead><tr><th>Cliente</th><th>RNC/Cédula</th><th>Desde</th></tr></thead>
          <tbody>
            {data.map(c => <tr key={c._id}><td>{c.name}</td><td>{c.rnc || c.cedula || '—'}</td><td>{dateStr(c.createdAt)}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin clientes nuevos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'inactive-customers':
      return (
        <table className="report-table">
          <thead><tr><th>Cliente</th><th>RNC/Cédula</th><th>Teléfono</th></tr></thead>
          <tbody>
            {data.map(c => <tr key={c._id}><td>{c.name}</td><td>{c.rnc || c.cedula || '—'}</td><td>{c.phone || '—'}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">No hay clientes inactivos.</td></tr>}
          </tbody>
        </table>
      );

    case 'ar-aging':
      return (
        <table className="report-table">
          <thead>
            <tr><th>Cliente</th><th className="text-end">Al día</th><th className="text-end">1-30</th><th className="text-end">31-60</th><th className="text-end">61-90</th><th className="text-end">90+</th><th className="text-end">Total</th></tr>
          </thead>
          <tbody>
            {(data.rows || []).map((r, i) => (
              <tr key={i}>
                <td>{r.client}</td><td className="text-end">{money(r.current)}</td><td className="text-end">{money(r.d1_30)}</td>
                <td className="text-end">{money(r.d31_60)}</td><td className="text-end">{money(r.d61_90)}</td>
                <td className="text-end">{money(r.d90plus)}</td><td className="text-end">{money(r.total)}</td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={7} className="text-center">No hay cuentas por cobrar pendientes.</td></tr>}
          </tbody>
          {data.totals && (data.rows || []).length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td>Total</td><td className="text-end">{money(data.totals.current)}</td><td className="text-end">{money(data.totals.d1_30)}</td>
                <td className="text-end">{money(data.totals.d31_60)}</td><td className="text-end">{money(data.totals.d61_90)}</td>
                <td className="text-end">{money(data.totals.d90plus)}</td><td className="text-end">{money(data.totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      );

    case 'customer-statement':
      return (
        <table className="report-table">
          <thead><tr><th>Fecha</th><th>Factura</th><th>Descripción</th><th className="text-end">Total</th><th className="text-end">Pagado</th><th className="text-end">Balance</th></tr></thead>
          <tbody>
            {data.invoices.map((inv, i) => (
              <tr key={i}><td>{dateStr(inv.date)}</td><td>{inv.number}</td><td>{inv.description}</td><td className="text-end">{money(inv.total)}</td><td className="text-end">{money(inv.amountPaid)}</td><td className="text-end">{money(inv.balance)}</td></tr>
            ))}
            {data.invoices.length === 0 && <tr><td colSpan={6} className="text-center">Sin facturas con saldo pendiente.</td></tr>}
          </tbody>
          {data.invoices.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}><td colSpan={5}>Balance Total</td><td className="text-end">{money(data.totalBalance)}</td></tr>
            </tfoot>
          )}
        </table>
      );

    case 'outstanding-invoices':
      return (
        <table className="report-table">
          <thead><tr><th>Factura</th><th>Cliente</th><th className="text-end">Total</th><th className="text-end">Pagado</th><th className="text-end">Adeudado</th><th>Vencimiento</th><th>Días vencido</th></tr></thead>
          <tbody>
            {data.map((o, i) => (
              <tr key={i}><td>{o.number}</td><td>{o.client}</td><td className="text-end">{money(o.total)}</td><td className="text-end">{money(o.amountPaid)}</td><td className="text-end">{money(o.owed)}</td><td>{dateStr(o.dueDate)}</td><td>{o.daysOverdue > 0 ? o.daysOverdue : '—'}</td></tr>
            ))}
            {data.length === 0 && <tr><td colSpan={7} className="text-center">No hay cuentas por cobrar pendientes.</td></tr>}
          </tbody>
        </table>
      );

    case 'payment-register':
      return (
        <table className="report-table">
          <thead><tr><th>Fecha</th><th>Factura</th><th>Cliente</th><th className="text-end">Monto</th><th>Método</th></tr></thead>
          <tbody>
            {data.map((p, i) => <tr key={i}><td>{dateStr(p.date)}</td><td>{p.invoiceNumber}</td><td>{p.client}</td><td className="text-end">{money(p.amount)}</td><td>{p.method}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={5} className="text-center">Sin pagos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    // One block per client: each recibo de ingreso (an ingreso = the payment rows sharing a receipt) with what it paid.
    // "Recibido" is new money; saldo a favor used is shown apart and never added to it.
    case 'receipts-by-client': {
      const clients = data.clients || [];
      const t = data.totals || {};
      const anyCredit = t.creditUsed > 0.005;
      const cols = anyCredit ? 7 : 6;
      const method = (r) => `${RECEIPT_METHOD_LABELS[r.method] || r.method}${r.account ? ` · ${r.account}` : ''}`;
      const appliedTo = (r) => [
        ...r.applied.map(a => `${a.number} · ${money(a.amount)}`),
        ...(r.advance > 0.005 ? [`Anticipo · ${money(r.advance)}`] : []),
        ...(r.transport > 0.005 ? [`Transporte · ${money(r.transport)}`] : []),
      ];
      if (clients.length === 0) {
        return <table className="report-table"><tbody><tr><td className="text-center">Sin recibos de ingreso en este rango.</td></tr></tbody></table>;
      }
      return (
        <>
          {clients.map((c, ci) => (
            <table className="report-table" key={ci} style={{ marginBottom: 18 }}>
              <thead>
                <tr><th colSpan={cols} style={{ textAlign: 'left' }}>{c.client}{c.rnc ? ` — RNC/Cédula ${c.rnc}` : ''}</th></tr>
                <tr>
                  <th>Recibo</th><th>Fecha</th><th>Forma de pago</th><th>Aplicado a</th><th>Nota</th>
                  <th className="text-end">Recibido</th>{anyCredit && <th className="text-end">Saldo a favor usado</th>}
                </tr>
              </thead>
              <tbody>
                {c.receipts.map((r, i) => (
                  <tr key={i}>
                    <td>{r.number || '—'}</td>
                    <td>{dateStr(r.date)}</td>
                    <td>{r.cash > 0.005 ? method(r) : 'Saldo a favor'}</td>
                    <td>{appliedTo(r).map((line, k) => <div key={k} style={{ whiteSpace: 'nowrap' }}>{line}</div>)}</td>
                    <td>{[r.reference, r.notes].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="text-end">{r.cash > 0.005 ? money(r.cash) : '—'}</td>
                    {anyCredit && <td className="text-end">{r.creditUsed > 0.005 ? money(r.creditUsed) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td colSpan={5}>Total {c.client} ({c.totals.count} {c.totals.count === 1 ? 'recibo' : 'recibos'})</td>
                  <td className="text-end">{money(c.totals.cash)}</td>
                  {anyCredit && <td className="text-end">{money(c.totals.creditUsed)}</td>}
                </tr>
              </tfoot>
            </table>
          ))}
          {clients.length > 1 && (
            <table className="report-table" style={{ width: 'auto', minWidth: '45%', marginLeft: 'auto' }}>
              <tbody>
                <tr><td>Clientes:</td><td className="text-end">{clients.length}</td></tr>
                <tr><td>Recibos:</td><td className="text-end">{t.count}</td></tr>
                {t.advance > 0.005 && <tr><td>De ello, anticipos (saldo a favor):</td><td className="text-end">{money(t.advance)}</td></tr>}
                {anyCredit && <tr><td>Saldo a favor usado (no es dinero nuevo):</td><td className="text-end">{money(t.creditUsed)}</td></tr>}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, fontSize: '1.05em' }}><td>Total recibido:</td><td className="text-end">{money(t.cash)}</td></tr>
              </tfoot>
            </table>
          )}
        </>
      );
    }

    case 'collections':
      return (
        <table className="report-table">
          <thead><tr><th>Período</th><th className="text-end">Total</th><th className="text-end"># Pagos</th></tr></thead>
          <tbody>
            {data.map((c, i) => <tr key={i}><td>{c.period}</td><td className="text-end">{money(c.total)}</td><td className="text-end">{c.count}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin cobros en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'dso':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{data.dso != null ? data.dso.toFixed(1) : '—'}</div>
          <div className="report-kpi-label">{data.sampleSize ? `Sobre ${data.sampleSize} facturas pagadas` : 'Sin facturas pagadas en este rango'}</div>
        </div>
      );

    case 'quotes-by-status': {
      const rows = data.rows || [];
      const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
      return (
        <table className="report-table">
          <thead><tr><th>Estado</th><th className="text-end"># Cotizaciones</th><th className="text-end">Valor Total</th></tr></thead>
          <tbody>
            {rows.map((r, i) => <tr key={i}><td>{STATUS_LABELS[r.status] || r.status}</td><td className="text-end">{r.count}</td><td className="text-end">{money(r.total)}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan={3} className="text-center">Sin cotizaciones en este rango.</td></tr>}
          </tbody>
          {totalCount > 0 && (
            <tfoot><tr style={{ fontWeight: 700 }}><td>Total</td><td className="text-end">{totalCount}</td><td className="text-end">{money(rows.reduce((s, r) => s + r.total, 0))}</td></tr></tfoot>
          )}
        </table>
      );
    }

    case 'conduces': {
      const rows = data.rows || [];
      const totalUnits = rows.reduce((s, c) => s + (Number(c.units) || 0), 0);
      // Only conduces made with prices have an amount to add up.
      const priced = rows.filter(c => c.showPrices);
      return (
        <table className="report-table report-table-centered">
          <thead><tr><th>No.</th><th>Cliente</th><th>Fecha</th><th>Productos</th><th>Total</th><th>Estado</th></tr></thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={i}>
                <td>{c.number}</td><td>{c.client}</td><td>{dateStr(c.date)}</td>
                <td>{Number(c.units || 0).toLocaleString('es-DO', { maximumFractionDigits: 2 })}</td>
                <td>{c.showPrices ? money(c.total) : <span className="text-muted">Sin precios</span>}</td>
                <td>
                  {CONDUCE_STATUS_LABELS[c.status] || c.status}
                  {c.status === 'delivered' && c.deliveredAt && <div className="small text-muted">{dateStr(c.deliveredAt)}</div>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center">Sin conduces en este rango.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={3} className="report-total-label">Total — {rows.length} conduce{rows.length === 1 ? '' : 's'}</td>
                <td>{totalUnits.toLocaleString('es-DO', { maximumFractionDigits: 2 })}</td>
                <td>{priced.length > 0 ? money(priced.reduce((s, c) => s + (c.total || 0), 0)) : ''}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      );
    }

    case 'expenses-summary':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{money(data.total)}</div>
          <div className="report-kpi-label">{data.count} gasto{data.count === 1 ? '' : 's'} registrado{data.count === 1 ? '' : 's'} en este rango</div>
        </div>
      );

    case 'expenses-by-category':
      return (
        <table className="report-table">
          <thead><tr><th>Categoría</th><th className="text-end"># Gastos</th><th className="text-end">Total</th></tr></thead>
          <tbody>
            {data.map((e, i) => <tr key={i}><td>{e.category}</td><td className="text-end">{e.count}</td><td className="text-end">{money(e.total)}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={3} className="text-center">Sin gastos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'product-margin':
      return (
        <table className="report-table">
          <thead><tr><th>Producto</th><th className="text-end">Cant.</th><th className="text-end">Ingreso</th><th className="text-end">Costo</th><th className="text-end">Margen</th></tr></thead>
          <tbody>
            {data.map((p, i) => (
              <tr key={i}><td>{p.name}</td><td className="text-end">{p.quantity}</td><td className="text-end">{money(p.revenue)}</td><td className="text-end">{money(p.cogs)}</td><td className="text-end">{money(p.grossProfit)} ({pctStr(p.marginPct)})</td></tr>
            ))}
            {data.length === 0 && <tr><td colSpan={5} className="text-center">Sin datos en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'profit-and-loss':
      return (
        <table className="report-table">
          <tbody>
            <tr><td>Ingresos</td><td className="text-end">{money(data.revenue)}</td></tr>
            <tr><td>Costo de Ventas (COGS)</td><td className="text-end">({money(data.cogs)})</td></tr>
            <tr style={{ fontWeight: 600 }}><td>Ganancia Bruta</td><td className="text-end">{money(data.grossProfit)}</td></tr>
            <tr><td>Gastos Operativos</td><td className="text-end">({money(data.operatingExpenses)})</td></tr>
            <tr style={{ fontWeight: 700 }}><td>Ganancia Neta</td><td className="text-end">{money(data.netProfit)}</td></tr>
          </tbody>
        </table>
      );

    // Like the old system's listing: one block per tipo de comprobante with its total, then the gran total. Descuento and
    // Flete are always 0 in Ventas (prices are net, there's no freight field) — kept so the columns match.
    case 'ncf-listing': {
      const groups = data.groups || [];
      const num = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
      const cols = 10;
      return (
        <table className="report-table report-table-ncf">
          <thead>
            <tr>
              <th>Fecha</th><th>Factura</th><th>NCF</th><th>Nombre</th>
              <th className="text-end">Base Imponible</th><th className="text-end">Descuento</th><th className="text-end">ITBIS</th>
              <th className="text-end">Flete</th><th className="text-end">Total Factura</th><th>Est.</th>
            </tr>
          </thead>
          {groups.map(g => (
            <tbody key={g.tipo}>
              <tr><td colSpan={cols} style={{ fontWeight: 700, paddingTop: 10 }}>Tipo NCF: {NCF_TIPO_NAMES[g.tipo] || g.tipo}</td></tr>
              {g.rows.map((r, i) => {
                const voided = r.status === 'void';
                return (
                  <tr key={i} style={voided ? { color: '#8892a0' } : undefined}>
                    <td>{dateStr(r.date)}</td><td>{r.number}</td><td>{r.ncf}</td><td className="ncf-name">{r.client}</td>
                    <td className="text-end">{num(r.subtotal)}</td><td className="text-end">{num(0)}</td><td className="text-end">{num(r.itbis)}</td>
                    <td className="text-end">{num(0)}</td><td className="text-end">{num(r.total)}</td>
                    <td>{NCF_STATUS_LABELS[r.status] || r.status}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, borderTop: '1.5px solid #0B1B29' }}>
                <td colSpan={4}>Total: {NCF_TIPO_NAMES[g.tipo] || g.tipo} ({g.rows.length})</td>
                <td className="text-end">{num(g.totals.subtotal)}</td><td className="text-end">{num(0)}</td><td className="text-end">{num(g.totals.itbis)}</td>
                <td className="text-end">{num(0)}</td><td className="text-end">{num(g.totals.total)}</td><td />
              </tr>
            </tbody>
          ))}
          {groups.length === 0 && <tbody><tr><td colSpan={cols} className="text-center">Sin comprobantes con NCF en este rango.</td></tr></tbody>}
          {groups.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700, fontSize: '1.05em', borderTop: '2px solid #0B1B29' }}>
                <td colSpan={4} className="text-end">Gran Total:</td>
                <td className="text-end">{num(data.totals.subtotal)}</td><td className="text-end">{num(0)}</td><td className="text-end">{num(data.totals.itbis)}</td>
                <td className="text-end">{num(0)}</td><td className="text-end">{num(data.totals.total)}</td><td />
              </tr>
            </tfoot>
          )}
        </table>
      );
    }

    case 'dgii-607':
      return (
        <table className="report-table">
          <thead><tr><th>No.</th><th>NCF</th><th>Tipo</th><th>Fecha</th><th>Cliente</th><th>RNC/Cédula</th><th className="text-end">Subtotal</th><th className="text-end">ITBIS</th><th className="text-end">Total</th><th>Estado</th></tr></thead>
          <tbody>
            {data.map((s, i) => (
              <tr key={i}><td>{s.number}</td><td>{s.ncf || '—'}</td><td>{s.tipoEcf}</td><td>{dateStr(s.date)}</td><td>{s.client}</td><td>{s.clientRnc || '—'}</td><td className="text-end">{money(s.subtotal)}</td><td className="text-end">{money(s.itbis)}</td><td className="text-end">{money(s.total)}</td><td>{s.status}</td></tr>
            ))}
            {data.length === 0 && <tr><td colSpan={10} className="text-center">Sin facturas en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'dgii-608':
      return (
        <table className="report-table">
          <thead><tr><th>No.</th><th>NCF</th><th>Tipo</th><th>Anulada</th><th>Cliente</th><th className="text-end">Total</th></tr></thead>
          <tbody>
            {data.map((v, i) => <tr key={i}><td>{v.number}</td><td>{v.ncf || '—'}</td><td>{v.tipoEcf}</td><td>{dateStr(v.voidedAt)}</td><td>{v.client}</td><td className="text-end">{money(v.total)}</td></tr>)}
            {data.length === 0 && <tr><td colSpan={6} className="text-center">Sin comprobantes anulados en este rango.</td></tr>}
          </tbody>
        </table>
      );

    case 'itbis-summary':
      return (
        <table className="report-table">
          <thead><tr><th>Tasa</th><th className="text-end">Base</th><th className="text-end">ITBIS</th></tr></thead>
          <tbody>
            {(data.rows || []).map((r, i) => <tr key={i}><td>{r.bucket === 'exento' ? 'Exento' : `${r.bucket}%`}</td><td className="text-end">{money(r.base)}</td><td className="text-end">{money(r.itbis)}</td></tr>)}
            {(data.rows || []).length === 0 && <tr><td colSpan={3} className="text-center">Sin datos en este rango.</td></tr>}
          </tbody>
          {(data.rows || []).length > 0 && (
            <tfoot><tr style={{ fontWeight: 700 }}><td colSpan={2}>Total ITBIS</td><td className="text-end">{money(data.totalItbis)}</td></tr></tfoot>
          )}
        </table>
      );

    case 'repeat-client-rate':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{data.rate != null ? `${(data.rate * 100).toFixed(0)}%` : '—'}</div>
          <div className="report-kpi-label">{data.repeatClients} de {data.totalClients} clientes compraron más de una vez en este rango</div>
        </div>
      );

    case 'revenue-concentration':
      return (
        <>
          <div className="report-kpi report-kpi-compact">
            <div className="report-kpi-value">{data.concentration != null ? `${(data.concentration * 100).toFixed(0)}%` : '—'}</div>
            <div className="report-kpi-label">del ingreso total ({money(data.totalRevenue)}) viene de tus 5 clientes principales</div>
          </div>
          <table className="report-table">
            <thead><tr><th>Cliente</th><th className="text-end">Ingreso</th></tr></thead>
            <tbody>
              {(data.topClients || []).map((c, i) => <tr key={i}><td>{c.name}</td><td className="text-end">{money(c.total)}</td></tr>)}
              {(data.topClients || []).length === 0 && <tr><td colSpan={2} className="text-center">Sin datos en este rango.</td></tr>}
            </tbody>
          </table>
        </>
      );

    case 'reorder-cycle':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{data.avgDays != null ? data.avgDays.toFixed(0) : '—'}</div>
          <div className="report-kpi-label">días en promedio entre compras, sobre {data.clientsWithRepeatOrders} cliente(s) con pedidos repetidos</div>
        </div>
      );

    case 'itbis-payable':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{money(data.itbisPayable)}</div>
          <div className="report-kpi-label">ITBIS cobrado en este rango, pendiente de declarar a DGII</div>
        </div>
      );

    case 'avg-invoice-value':
      return (
        <div className="report-kpi report-kpi-compact">
          <div className="report-kpi-value">{money(data.avgInvoiceValue)}</div>
          <div className="report-kpi-label">promedio sobre {data.invoiceCount} factura{data.invoiceCount === 1 ? '' : 's'} en este rango</div>
        </div>
      );

    default:
      return <div>Tipo de reporte no reconocido.</div>;
  }
}

// Only reports with a single standalone grand-total (not already part of a
// table's own tfoot, like ar-aging/quotes-by-status/itbis-summary/customer-statement)
// need this — it's pinned near the bottom of the page alongside the footer.
// No current report type needs it; kept as the extension point for one that would.
function ReportGrandTotal() {
  return null;
}

// Wide listings printed on landscape letter (see .report-landscape in the CSS).
const LANDSCAPE_REPORTS = new Set(['ncf-listing']);

// Letter height at 96 px/in, and how much longer a page break makes the printout than the screen layout this is
// measured on (the table header repeats on every page, and a row that would be cut moves whole to the next one).
const PAGE_PX = 1056;
const BREAK_DRIFT_PX = 90;

// For the signed report, printed at true size: makes the paper a whole number of pages tall, so the bottom block
// (signatures and seal, footer note) lands at the bottom of the last page instead of right under the table. If it
// doesn't fit under the table on that page, it goes to the bottom of the next one.
function pinBottomBlock(paper) {
  const block = paper?.querySelector('.report-bottom-block');
  if (!block) return;
  paper.style.minHeight = '0';
  const top = block.getBoundingClientRect().top - paper.getBoundingClientRect().top;
  const tail = block.offsetHeight + parseFloat(getComputedStyle(paper).paddingBottom || 0);
  let printTop = top;
  for (let i = 0; i < 3; i += 1) printTop = top + BREAK_DRIFT_PX * Math.floor(printTop / PAGE_PX);
  let pages = Math.floor(printTop / PAGE_PX) + 1;
  if (printTop + tail > pages * PAGE_PX) pages += 1;
  paper.style.minHeight = `${pages * PAGE_PX}px`;
}

const ReportPrintView = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const clientId = searchParams.get('client');
  const status = searchParams.get('status');
  const tipo = searchParams.get('tipo');

  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [clientName, setClientName] = useState('');
  const [error, setError] = useState('');
  const paperRef = useRef(null);

  // Saved as PDF under the report's plain name, "Reporte de Cobros" (utils/fileName.js).
  useFileName(id ? reportName(REPORT_TITLES[id]) : null);

  const hasKpiDetail = !!KPI_CHART_BUILDERS[id];
  const signed = id === 'conduces' || id === 'receipts-by-client';
  const landscape = LANDSCAPE_REPORTS.has(id);

  useEffect(() => {
    if (!id) {
      setError('Falta especificar el reporte a generar.');
      return;
    }
    Promise.all([
      fetchReportData(id, { from, to, clientId, status, tipo }),
      hasKpiDetail ? fetchDetailData(id, { from, to }) : Promise.resolve(null),
    ])
      .then(([result, detailResult]) => {
        setData(result);
        setDetail(detailResult);
        if (['customer-statement', 'conduces', 'receipts-by-client'].includes(id)) setClientName(result.client?.name || '');
      })
      .catch(err => {
        console.error('Error loading report:', err);
        setError(err.response?.data?.message || 'No se pudo cargar el reporte.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only the report that carries signatures (see .report-signed in the CSS). Measured again right before printing:
  // the logo may not have loaded the first time.
  useEffect(() => {
    if (!data || !signed) return undefined;
    const pin = () => pinBottomBlock(paperRef.current);
    pin();
    window.addEventListener('beforeprint', pin);
    return () => window.removeEventListener('beforeprint', pin);
  }, [data, signed]);

  useEffect(() => {
    if (data) {
      // Charts need an extra beat to finish rendering onto <canvas> before
      // window.print() rasterizes the page.
      const t = setTimeout(() => window.print(), hasKpiDetail ? 900 : 350);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!data) return <Loading page label="Cargando reporte..." />;

  return (
    <div className={`report-page-wrapper${signed ? ' report-signed' : ''}${landscape ? ' report-landscape' : ''}`}>
      {/* @page can't be scoped to a class: only rendered for a landscape report, it overrides the CSS file's portrait one. */}
      {landscape && <style>{'@page { size: letter landscape; margin: 0.4in; }'}</style>}
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="report-paper" ref={paperRef}>
        <div className="report-header-row">
          <div className="report-company-block">
            <img src="/logo-print.png" alt="Intmed" className="report-logo" />
            <div style={{ fontWeight: 700, color: '#0B1B29' }}>{COMPANY_INFO.name}</div>
            <div>RNC: {COMPANY_INFO.rnc}</div>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            <div>{COMPANY_INFO.website}</div>
          </div>
          <div className="report-meta-block">
            <div className="report-title">{REPORT_TITLES[id] || 'Reporte'}</div>
            {clientName && <div>Cliente: {clientName}</div>}
            {id === 'ncf-listing' && <div>Solo: {tipo ? NCF_TIPO_NAMES[tipo] || tipo : 'Todos los comprobantes'}</div>}
            {from && to && <div>Período: {dateStr(from)} — {dateStr(to)}</div>}
            <div>Generado: {dateStr(new Date())}</div>
          </div>
        </div>

        <div className="report-body">
          <ReportBody id={id} data={data} />
          <KpiDetailSection id={id} detail={detail} />
        </div>

        <div className="report-bottom-block">
          <ReportGrandTotal id={id} data={data} />
          {/* Recibos for every client have no single client to sign: the second line is whoever reviews it. */}
          {signed && <SignatureLines clientName={clientName} clientLabel={id === 'receipts-by-client' && !clientId ? 'Revisado por' : undefined} />}
          <div className="report-footer-note">
            {COMPANY_INFO.name} — reporte de uso interno, no es un documento fiscal.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPrintView;
